import { doc, setDoc, updateDoc, UpdateData, DocumentData } from 'firebase/firestore';
import { db } from '../../firebase';
import { FullProject, ProjectMetadata, Task, InboxItem } from '../../types';

export interface BranchSampleProjectData {
  name: string;
  metadata?: Partial<ProjectMetadata> | Record<string, unknown>;
  tasks: Task[];
  contacts?: Task[];
}

export interface SecondarySampleProjectData {
  project: FullProject;
  tasks: Task[];
}

export interface SeedInboxAndSampleProjectsParams {
  finalProjects: FullProject[];
  userId: string;
  tenantId: string;
  INBOX_PROJECT_ID: string;
  COMM_PROJECT_ID: string;
  commSampleDismissed: boolean;
  DEFAULT_INBOX_ITEMS: InboxItem[];
  DEFAULT_PROCESSED_ITEMS: InboxItem[];
  getSampleDataForBranch: (branch: string, userId: string, tenantId: string, projectId: string) => BranchSampleProjectData;
  getSecondarySampleProject: (userId: string, tenantId: string, formatDate: (offsetDays: number) => string) => SecondarySampleProjectData;
  syncProjectToFirestore: (project: FullProject, isSilent?: boolean) => Promise<number | null>;
}

function buildDefaultContactGraphLinks(userId: string) {
  return [
    { source: `sample_contact_richter_${userId}`, target: `sample_contact_berger_${userId}`, type: 'Lenkung & Freigabe' },
    { source: `sample_contact_fischer_${userId}`, target: `sample_contact_berger_${userId}`, type: 'Stakeholder-Vertretung' },
    { source: `sample_contact_berger_${userId}`, target: `sample_contact_weber_${userId}`, type: 'Technische Abstimmung' },
    { source: `sample_contact_berger_${userId}`, target: `sample_contact_meier_${userId}`, type: 'Beteiligung & Moderation' },
    { source: `sample_contact_berger_${userId}`, target: `sample_contact_hoffmann_${userId}`, type: 'Kommunikation & Presse' },
    { source: `sample_contact_weber_${userId}`, target: `sample_contact_meier_${userId}`, type: 'Raumprogramm-Feedback' },
  ];
}

export async function ensureInboxAndSampleProjectsSeeded({
  finalProjects,
  userId,
  tenantId,
  INBOX_PROJECT_ID,
  COMM_PROJECT_ID,
  commSampleDismissed,
  DEFAULT_INBOX_ITEMS,
  DEFAULT_PROCESSED_ITEMS,
  getSampleDataForBranch,
  getSecondarySampleProject,
  syncProjectToFirestore,
}: SeedInboxAndSampleProjectsParams): Promise<void> {
  // 5. Ensure Inbox Project exists and is seeded with premium test data
  const existingInbox = finalProjects.find(p => p.id === INBOX_PROJECT_ID);
  let inboxProject: FullProject;
  if (!existingInbox) {
    const createdInbox: FullProject = {
      id: INBOX_PROJECT_ID,
      name: 'Nicht zugeordnet',
      tasks: [],
      lastModified: 1,
      color: 'bg-slate-500',
      workspace: 'professional',
      userId,
      tenantId,
      metadata: {
        inboxItems: DEFAULT_INBOX_ITEMS,
        processedItems: DEFAULT_PROCESSED_ITEMS,
        contactGraphLinks: buildDefaultContactGraphLinks(userId),
      },
      changelog: [],
    };
    finalProjects.push(createdInbox);
    syncProjectToFirestore(createdInbox);
    inboxProject = createdInbox;
  } else {
    inboxProject = existingInbox;
    const meta = inboxProject.metadata || {};
    const inboxSampleDismissed = meta.sampleDismissed === true || commSampleDismissed;
    const partialUpdate: UpdateData<DocumentData> = {};

    if (!inboxSampleDismissed) {
      if (!meta.inboxItems || meta.inboxItems.length === 0) {
        partialUpdate['metadata.inboxItems'] = DEFAULT_INBOX_ITEMS;
      }
      if (!meta.processedItems || meta.processedItems.length === 0) {
        partialUpdate['metadata.processedItems'] = DEFAULT_PROCESSED_ITEMS;
      }
      if (!meta.contactGraphLinks || meta.contactGraphLinks.length === 0) {
        partialUpdate['metadata.contactGraphLinks'] = buildDefaultContactGraphLinks(userId);
      }
    }

    if (Object.keys(partialUpdate).length > 0 && userId !== 'e2e_local_uid') {
      const inboxDocRef = doc(db, 'projects', INBOX_PROJECT_ID);
      updateDoc(inboxDocRef, partialUpdate).catch(err =>
        console.warn('[Init] Failed to seed inbox metadata fields:', err)
      );
    }
  }

  // 5b. Ensure Global Tenant Settings Project exists
  if (INBOX_PROJECT_ID !== 'inbox_inforum_' + tenantId) {
    const globalInboxId = 'inbox_inforum_' + tenantId;
    const globalInboxExists = finalProjects.some(p => p.id === globalInboxId);
    if (!globalInboxExists) {
      const globalInboxProj: FullProject = {
        id: globalInboxId,
        name: 'Nicht zugeordnet',
        tasks: [],
        lastModified: 1,
        color: 'bg-slate-500',
        workspace: 'professional',
        tenantId,
        metadata: {
          inboxItems: DEFAULT_INBOX_ITEMS,
          processedItems: [],
          vacations: [],
          categories_professional: [
            { id: 'cat_strategy', name: 'Strategie & Innovation', color: 'bg-indigo-500' },
            { id: 'cat_operations', name: 'Prozesse & Betrieb', color: 'bg-emerald-500' },
            { id: 'cat_infrastructure', name: 'IT & Infrastruktur', color: 'bg-cyan-500' },
          ],
          graphRootLabel: {
            professional: 'Zentrale Projektsteuerung',
            private: 'Mein privates Zentrum',
          },
          graphNodes: [
            { id: 'node_strategy', label: 'Strategie & Innovation', type: 'CATEGORY', level: 2, color: 'bg-indigo-500', workspace: 'professional' },
            { id: 'node_operations', label: 'Prozesse & Betrieb', type: 'CATEGORY', level: 2, color: 'bg-emerald-500', workspace: 'professional' },
            { id: 'node_infrastructure', label: 'IT & Infrastruktur', type: 'CATEGORY', level: 2, color: 'bg-cyan-500', workspace: 'professional' },
          ],
          graphLinks: [
            { source: 'root-anchor', target: 'node_strategy' },
            { source: 'root-anchor', target: 'node_operations' },
            { source: 'root-anchor', target: 'node_infrastructure' },
            { source: 'node_strategy', target: `sample_${userId}` },
            { source: 'node_infrastructure', target: `sample_portal_${userId}` },
          ],
          contactGraphLinks: buildDefaultContactGraphLinks(userId),
        },
        changelog: [],
      };
      finalProjects.push(globalInboxProj);
      syncProjectToFirestore(globalInboxProj);
    }
  }

  // 6. Ensure Sample Project exists if the user has no owned projects and hasn't dismissed it
  const userProjects = finalProjects.filter(
    p =>
      p.userId === userId &&
      p.id !== INBOX_PROJECT_ID &&
      p.id !== 'inbox_inforum' &&
      !p.id.startsWith('inbox_inforum_') &&
      !p.id.startsWith('comm_internal_') &&
      !p.id.startsWith('sample_') &&
      !p.isSample &&
      !p.metadata?.isSample &&
      p.id !== COMM_PROJECT_ID &&
      !p.archived
  );

  const inboxMeta = inboxProject?.metadata || {};
  const sampleProjDismissed = inboxMeta.sampleDismissed === true || commSampleDismissed;
  const sampleProjId = `sample_${userId}`;
  const sampleProjectExists = finalProjects.some(p => p.id === sampleProjId);

  if (userProjects.length === 0 && !sampleProjDismissed && !sampleProjectExists) {
    const sampleProjData = getSampleDataForBranch('public_sector', userId, tenantId, sampleProjId);
    const sampleProj: FullProject = {
      id: sampleProjId,
      name: sampleProjData.name,
      tasks: [],
      lastModified: Date.now(),
      color: 'bg-indigo-600',
      workspace: 'professional',
      userId,
      tenantId,
      isSample: true,
      categoryId: 'cat_strategy',
      metadata: sampleProjData.metadata,
      changelog: [],
    };

    if (userId !== 'e2e_local_uid') {
      try {
        await setDoc(doc(db, 'projects', sampleProjId), {
          name: sampleProj.name,
          color: sampleProj.color,
          workspace: sampleProj.workspace,
          userId: sampleProj.userId,
          tenantId: sampleProj.tenantId,
          isSample: true,
          categoryId: sampleProj.categoryId,
          metadata: sampleProj.metadata,
          lastModified: sampleProj.lastModified,
          changelog: [],
        });
      } catch (e) {
        console.error('[Init] Failed to save sample project doc:', e);
      }

      try {
        await Promise.all(
          sampleProjData.tasks.map((task: Task) => setDoc(doc(db, 'tasks', task.id.toString()), task))
        );
      } catch (e) {
        console.error('[Init] Failed to save sample tasks:', e);
      }
    }

    sampleProj.tasks = sampleProjData.tasks;
    finalProjects.push(sampleProj);

    // Also seed secondary sample project
    try {
      const formatDateSeed = (offsetDays: number) => {
        const d = new Date();
        d.setDate(d.getDate() + offsetDays);
        return d.toISOString().split('T')[0];
      };
      const secData = getSecondarySampleProject(userId, tenantId, formatDateSeed);
      const secProj = secData.project;
      if (userId !== 'e2e_local_uid') {
        await setDoc(doc(db, 'projects', secProj.id), {
          name: secProj.name,
          color: secProj.color,
          workspace: secProj.workspace,
          userId: secProj.userId,
          tenantId: secProj.tenantId,
          isSample: true,
          categoryId: secProj.categoryId,
          metadata: secProj.metadata,
          lastModified: secProj.lastModified,
          changelog: [],
        });
        await Promise.all(secData.tasks.map((t: Task) => setDoc(doc(db, 'tasks', t.id.toString()), t)));
      }
      secProj.tasks = secData.tasks;
      finalProjects.push(secProj);
    } catch (e) {
      console.error('[Init] Failed to save secondary sample project:', e);
    }
  }
}
